import { supabase } from "../supabase/client";
import type { AuthChangeEvent, Session } from "@supabase/supabase-js";
import {
  logSecurityEvent,
  isUserLockedOut,
  getRemainingLockoutTime,
  SecurityEventType,
  SecurityEventSeverity,
} from "../security/monitoring";
import { validateInput } from "../../utils/validation";

export interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  avatarUrl?: string;
}

export interface AuthError {
  message: string;
  code?: string;
}

class AuthService {
  private getAppBaseUrl(): string {
    const envUrl = process.env.REACT_APP_URL;
    const base = envUrl && envUrl.trim().length > 0 ? envUrl : "";
    const normalized = base ? base.replace(/\/$/, "") : "";
    return normalized || window.location.origin;
  }

  // Get user's IP address (client-side approximation)
  private getUserIP(): string {
    // In a real app, you'd get this from the server
    return "client-side";
  }

  // Get user agent
  private getUserAgent(): string {
    return navigator.userAgent;
  }

  // Sign up new user
  async signUp(
    email: string,
    password: string,
    firstName?: string,
    lastName?: string
  ): Promise<{
    user: User | null;
    error: AuthError | null;
    requiresEmailConfirmation: boolean;
  }> {
    try {
      // Validate inputs
      const emailValidation = validateInput(email, {
        required: true,
        email: true,
        maxLength: 255,
      });

      if (!emailValidation.isValid) {
        logSecurityEvent({
          type: SecurityEventType.SUSPICIOUS_ACTIVITY,
          severity: SecurityEventSeverity.MEDIUM,
          email,
          ip: this.getUserIP(),
          userAgent: this.getUserAgent(),
          details: {
            reason: "Invalid email format during signup",
            errors: emailValidation.errors,
          },
        });

        return {
          user: null,
          error: { message: emailValidation.errors.join(", ") },
          requiresEmailConfirmation: false,
        };
      }

      // Check if user is locked out
      if (isUserLockedOut(email)) {
        const remainingTime = getRemainingLockoutTime(email);

        logSecurityEvent({
          type: SecurityEventType.UNAUTHORIZED_ACCESS,
          severity: SecurityEventSeverity.HIGH,
          email,
          ip: this.getUserIP(),
          userAgent: this.getUserAgent(),
          details: {
            reason: "Attempted signup while locked out",
            remainingLockoutTime: remainingTime,
          },
        });

        return {
          user: null,
          error: {
            message: `Account temporarily locked. Try again in ${remainingTime} seconds.`,
            code: "ACCOUNT_LOCKED",
          },
          requiresEmailConfirmation: false,
        };
      }

      const emailRedirectTo = `${this.getAppBaseUrl()}/login?confirmed=1`;
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name: firstName,
            last_name: lastName,
          },
          emailRedirectTo,
        },
      });

      console.debug("[DEBUG] Supabase signup redirect", {
        email,
        emailRedirectTo,
      });

      if (error) {
        logSecurityEvent({
          type: SecurityEventType.LOGIN_FAILURE,
          severity: SecurityEventSeverity.MEDIUM,
          email,
          ip: this.getUserIP(),
          userAgent: this.getUserAgent(),
          details: {
            reason: "Supabase signup error",
            error: error.message,
            code: error.name,
          },
        });

        return {
          user: null,
          error: { message: error.message, code: error.name },
          requiresEmailConfirmation: false,
        };
      }

      if (!data.user) {
        logSecurityEvent({
          type: SecurityEventType.LOGIN_FAILURE,
          severity: SecurityEventSeverity.HIGH,
          email,
          ip: this.getUserIP(),
          userAgent: this.getUserAgent(),
          details: {
            reason: "Signup succeeded but no user returned",
          },
        });

        return {
          user: null,
          error: { message: "Failed to create user" },
          requiresEmailConfirmation: false,
        };
      }

      // Log successful signup
      logSecurityEvent({
        type: SecurityEventType.LOGIN_SUCCESS,
        severity: SecurityEventSeverity.LOW,
        userId: data.user.id,
        email: data.user.email,
        ip: this.getUserIP(),
        userAgent: this.getUserAgent(),
        details: {
          reason: "New user signup successful",
        },
      });

      const requiresEmailConfirmation = !data.session;
      console.debug("[DEBUG] Supabase signup response", {
        email: data.user.email,
        requiresEmailConfirmation,
      });

      return {
        user: {
          id: data.user.id,
          email: data.user.email!,
          firstName: data.user.user_metadata?.first_name,
          lastName: data.user.user_metadata?.last_name,
          avatarUrl: data.user.user_metadata?.avatar_url,
        },
        error: null,
        requiresEmailConfirmation,
      };
    } catch (err: any) {
      logSecurityEvent({
        type: SecurityEventType.LOGIN_FAILURE,
        severity: SecurityEventSeverity.HIGH,
        email,
        ip: this.getUserIP(),
        userAgent: this.getUserAgent(),
        details: {
          reason: "Unexpected error during signup",
          error: err.message,
        },
      });

      return {
        user: null,
        error: { message: err.message || "An unexpected error occurred" },
        requiresEmailConfirmation: false,
      };
    }
  }

  // Sign in existing user
  async signIn(
    email: string,
    password: string
  ): Promise<{ user: User | null; error: AuthError | null }> {
    try {
      // Log login attempt
      logSecurityEvent({
        type: SecurityEventType.LOGIN_ATTEMPT,
        severity: SecurityEventSeverity.LOW,
        email,
        ip: this.getUserIP(),
        userAgent: this.getUserAgent(),
        details: {
          timestamp: new Date().toISOString(),
        },
      });

      // Validate inputs
      const emailValidation = validateInput(email, {
        required: true,
        email: true,
        maxLength: 255,
      });

      if (!emailValidation.isValid) {
        logSecurityEvent({
          type: SecurityEventType.SUSPICIOUS_ACTIVITY,
          severity: SecurityEventSeverity.MEDIUM,
          email,
          ip: this.getUserIP(),
          userAgent: this.getUserAgent(),
          details: {
            reason: "Invalid email format during login",
            errors: emailValidation.errors,
          },
        });

        return {
          user: null,
          error: { message: emailValidation.errors.join(", ") },
        };
      }

      // Check if user is locked out
      if (isUserLockedOut(email)) {
        const remainingTime = getRemainingLockoutTime(email);

        logSecurityEvent({
          type: SecurityEventType.UNAUTHORIZED_ACCESS,
          severity: SecurityEventSeverity.HIGH,
          email,
          ip: this.getUserIP(),
          userAgent: this.getUserAgent(),
          details: {
            reason: "Attempted login while locked out",
            remainingLockoutTime: remainingTime,
          },
        });

        return {
          user: null,
          error: {
            message: `Account temporarily locked due to too many failed attempts. Try again in ${remainingTime} seconds.`,
            code: "ACCOUNT_LOCKED",
          },
        };
      }

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        logSecurityEvent({
          type: SecurityEventType.LOGIN_FAILURE,
          severity: SecurityEventSeverity.MEDIUM,
          email,
          ip: this.getUserIP(),
          userAgent: this.getUserAgent(),
          details: {
            reason: "Supabase authentication failed",
            error: error.message,
            code: error.name,
          },
        });

        return {
          user: null,
          error: { message: error.message, code: error.name },
        };
      }

      if (!data.user) {
        logSecurityEvent({
          type: SecurityEventType.LOGIN_FAILURE,
          severity: SecurityEventSeverity.HIGH,
          email,
          ip: this.getUserIP(),
          userAgent: this.getUserAgent(),
          details: {
            reason: "Authentication succeeded but no user returned",
          },
        });

        return { user: null, error: { message: "Failed to sign in" } };
      }

      // Log successful login
      logSecurityEvent({
        type: SecurityEventType.LOGIN_SUCCESS,
        severity: SecurityEventSeverity.LOW,
        userId: data.user.id,
        email: data.user.email,
        ip: this.getUserIP(),
        userAgent: this.getUserAgent(),
        details: {
          reason: "User login successful",
          lastSignIn: data.user.last_sign_in_at,
        },
      });

      return {
        user: {
          id: data.user.id,
          email: data.user.email!,
          firstName: data.user.user_metadata?.first_name,
          lastName: data.user.user_metadata?.last_name,
          avatarUrl: data.user.user_metadata?.avatar_url,
        },
        error: null,
      };
    } catch (err: any) {
      logSecurityEvent({
        type: SecurityEventType.LOGIN_FAILURE,
        severity: SecurityEventSeverity.HIGH,
        email,
        ip: this.getUserIP(),
        userAgent: this.getUserAgent(),
        details: {
          reason: "Unexpected error during login",
          error: err.message,
        },
      });

      return {
        user: null,
        error: { message: err.message || "An unexpected error occurred" },
      };
    }
  }

  // Sign out current user
  async signOut(): Promise<{ error: AuthError | null }> {
    try {
      // Get current user info before signing out
      const currentUser = await this.getCurrentUser();

      const { error } = await supabase.auth.signOut();

      if (error) {
        logSecurityEvent({
          type: SecurityEventType.LOGOUT,
          severity: SecurityEventSeverity.MEDIUM,
          userId: currentUser?.id,
          email: currentUser?.email,
          ip: this.getUserIP(),
          userAgent: this.getUserAgent(),
          details: {
            reason: "Logout failed",
            error: error.message,
          },
        });

        return { error: { message: error.message, code: error.name } };
      }

      // Log successful logout
      logSecurityEvent({
        type: SecurityEventType.LOGOUT,
        severity: SecurityEventSeverity.LOW,
        userId: currentUser?.id,
        email: currentUser?.email,
        ip: this.getUserIP(),
        userAgent: this.getUserAgent(),
        details: {
          reason: "User logout successful",
        },
      });

      // Clear any local storage
      localStorage.removeItem("appSettings"); // Will be replaced with Supabase later

      return { error: null };
    } catch (err: any) {
      logSecurityEvent({
        type: SecurityEventType.LOGOUT,
        severity: SecurityEventSeverity.HIGH,
        ip: this.getUserIP(),
        userAgent: this.getUserAgent(),
        details: {
          reason: "Unexpected error during logout",
          error: err.message,
        },
      });

      return {
        error: { message: err.message || "An unexpected error occurred" },
      };
    }
  }

  // Get current user
  async getCurrentUser(): Promise<User | null> {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        return null;
      }

      return {
        id: user.id,
        email: user.email!,
        firstName: user.user_metadata?.first_name,
        lastName: user.user_metadata?.last_name,
        avatarUrl: user.user_metadata?.avatar_url,
      };
    } catch (err) {
      // Ignore AbortError - this happens during React StrictMode double-mounting
      if (err instanceof Error && err.name === "AbortError") {
        return null;
      }
      console.error("Error getting current user:", err);
      return null;
    }
  }

  // Check if user is authenticated
  async isAuthenticated(): Promise<boolean> {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      return !!session;
    } catch (err) {
      console.error("Error checking authentication:", err);
      return false;
    }
  }

  // Update user profile
  async updateProfile(updates: {
    firstName?: string;
    lastName?: string;
    avatarUrl?: string;
  }): Promise<{ error: AuthError | null }> {
    try {
      const { error } = await supabase.auth.updateUser({
        data: {
          first_name: updates.firstName,
          last_name: updates.lastName,
          avatar_url: updates.avatarUrl,
        },
      });

      if (error) {
        return { error: { message: error.message, code: error.name } };
      }

      return { error: null };
    } catch (err: any) {
      return {
        error: { message: err.message || "An unexpected error occurred" },
      };
    }
  }

  // Reset password request
  async resetPassword(email: string): Promise<{ error: AuthError | null }> {
    try {
      // Validate email
      const emailValidation = validateInput(email, {
        required: true,
        email: true,
        maxLength: 255,
      });

      if (!emailValidation.isValid) {
        logSecurityEvent({
          type: SecurityEventType.SUSPICIOUS_ACTIVITY,
          severity: SecurityEventSeverity.MEDIUM,
          email,
          ip: this.getUserIP(),
          userAgent: this.getUserAgent(),
          details: {
            reason: "Invalid email format during password reset",
            errors: emailValidation.errors,
          },
        });

        return {
          error: { message: emailValidation.errors.join(", ") },
        };
      }

      // Check if user is locked out
      if (isUserLockedOut(email)) {
        const remainingTime = getRemainingLockoutTime(email);

        logSecurityEvent({
          type: SecurityEventType.UNAUTHORIZED_ACCESS,
          severity: SecurityEventSeverity.HIGH,
          email,
          ip: this.getUserIP(),
          userAgent: this.getUserAgent(),
          details: {
            reason: "Attempted password reset while locked out",
            remainingLockoutTime: remainingTime,
          },
        });

        return {
          error: {
            message: `Account temporarily locked. Try again in ${remainingTime} seconds.`,
            code: "ACCOUNT_LOCKED",
          },
        };
      }

      const resetRedirectTo = `${this.getAppBaseUrl()}/reset-password`;
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: resetRedirectTo,
      });

      console.debug("[DEBUG] Supabase reset password redirect", {
        email,
        redirectTo: resetRedirectTo,
      });

      if (error) {
        logSecurityEvent({
          type: SecurityEventType.PASSWORD_RESET,
          severity: SecurityEventSeverity.MEDIUM,
          email,
          ip: this.getUserIP(),
          userAgent: this.getUserAgent(),
          details: {
            reason: "Password reset request failed",
            error: error.message,
            code: error.name,
          },
        });

        return { error: { message: error.message, code: error.name } };
      }

      // Log successful password reset request
      logSecurityEvent({
        type: SecurityEventType.PASSWORD_RESET,
        severity: SecurityEventSeverity.LOW,
        email,
        ip: this.getUserIP(),
        userAgent: this.getUserAgent(),
        details: {
          reason: "Password reset email sent successfully",
        },
      });

      return { error: null };
    } catch (err: any) {
      logSecurityEvent({
        type: SecurityEventType.PASSWORD_RESET,
        severity: SecurityEventSeverity.HIGH,
        email,
        ip: this.getUserIP(),
        userAgent: this.getUserAgent(),
        details: {
          reason: "Unexpected error during password reset",
          error: err.message,
        },
      });

      return {
        error: { message: err.message || "An unexpected error occurred" },
      };
    }
  }

  // Update password
  async updatePassword(
    newPassword: string
  ): Promise<{ error: AuthError | null }> {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) {
        return { error: { message: error.message, code: error.name } };
      }

      return { error: null };
    } catch (err: any) {
      return {
        error: { message: err.message || "An unexpected error occurred" },
      };
    }
  }

  // Subscribe to auth state changes
  onAuthStateChange(callback: (user: User | null) => void) {
    return supabase.auth.onAuthStateChange(
      (event: AuthChangeEvent, session: Session | null) => {
        if (session?.user) {
          callback({
            id: session.user.id,
            email: session.user.email!,
            firstName: session.user.user_metadata?.first_name,
            lastName: session.user.user_metadata?.last_name,
            avatarUrl: session.user.user_metadata?.avatar_url,
          });
        } else {
          callback(null);
        }
      }
    );
  }
}

export const authService = new AuthService();
